import React, { useEffect, useState } from "react";
import {
  Card,
  CardBody,
  CardFooter,
  Button,
  Dialog,
} from "@material-tailwind/react";
import { MathJax, MathJaxContext } from "better-react-mathjax";
import QuillEditorNew from "../QuilEditorNew";
import apiService from "../../services/apiService";
import { MdEdit } from "react-icons/md";
import useAuthStore from "../../store/authStore";

const mathJaxConfig = {
  tex: {
    inlineMath: [
      ["$", "$"],
      ["\\(", "\\)"],
    ],
    displayMath: [
      ["$$", "$$"],
      ["\\[", "\\]"],
    ],
  },
};

export default function ModalBody({
  question,
  onSave,
  cancelEdit,
  questionType,
  open,
}) {
  const { client } = useAuthStore();

  const [ques, setQues] = useState(question?.ques || "");
  const [options, setOptions] = useState(
    Array.isArray(question?.options) ? question.options : [],
  );
  const [image, setImage] = useState(question?.image || null);
  const [editState, setEditState] = useState(null);
  const [optionIndex, setOptionIndex] = useState(null);
  const [contentType, setContentType] = useState(null);
  const [enableSave, setEnableSave] = useState(false);
  const [editorContent, setEditorContent] = useState("");

  useEffect(() => {
    setQues(question?.ques || "");
    setOptions(Array.isArray(question?.options) ? question.options : []);
    setImage(question?.image || null);
    setEditState(null);
    setOptionIndex(null);
    setContentType(null);
    setEnableSave(false);
    setEditorContent("");
  }, [question]);

  function getS3ImageUrl(imageName) {
    const s3Dir = client?.s3Dir || "435abcschool";
    const qSetId = question?.qSetId || question?.qSet || "";

    if (!qSetId) return imageName;

    return `https://s3.us-west-004.backblazeb2.com/s3file/${s3Dir}/QSet/${qSetId}/${imageName}`;
  }

  function renderTextWithTags(text) {
    if (text === null || text === undefined) return "";

    let value = String(text);

    // Markdown image: ![](image-xxx.png)
    value = value.replace(
      /!\[[^\]]*\]\((image-[\w.-]+\.(png|jpg|jpeg|webp|gif))\)/gi,
      (match, imageName) => {
        return `<br/><img src="${getS3ImageUrl(
          imageName,
        )}" alt="${imageName}" style="max-width:100%;height:auto;margin:8px 0;" />`;
      },
    );

    // Plain image text: image-xxx.png
    value = value.replace(
      /(^|[\s>])(image-[\w.-]+\.(png|jpg|jpeg|webp|gif))(?=($|[\s<]))/gi,
      (match, prefix, imageName) => {
        return `${prefix}<br/><img src="${getS3ImageUrl(
          imageName,
        )}" alt="${imageName}" style="max-width:100%;height:auto;margin:8px 0;" />`;
      },
    );

    return value;
  }

  function normalizeEditorContent(htmlString) {
    if (htmlString === null || htmlString === undefined) return "";

    const div = document.createElement("div");
    div.innerHTML = String(htmlString);

    // Quill usually wraps content in <p>...</p>
    if (div.childNodes.length === 1 && div.firstChild?.tagName === "P") {
      return div.firstChild.innerHTML || "";
    }

    return div.innerHTML || div.textContent || "";
  }

  function checkForSave() {
    const content = normalizeEditorContent(editorContent);

    let newQues = ques;
    let newOptions = [...options];
    let newImage = image;

    if (contentType === "img") {
      newImage = content;
      setImage(content);
    } else if (contentType === "ques") {
      newQues = content;
      setQues(content);
    } else if (contentType === "opt" && optionIndex !== null) {
      newOptions[optionIndex] = content;
      setOptions(newOptions);
    }

    const oldObj = {
      id: question.id,
      num: question.num,
      ques: question.ques || "",
      options: Array.isArray(question.options) ? question.options : [],
      image: question.image || null,
    };

    const newObj = {
      id: question.id,
      num: question.num,
      ques: newQues,
      options: newOptions,
      image: newImage || null,
    };

    setEnableSave(JSON.stringify(oldObj) !== JSON.stringify(newObj));

    setContentType(null);
    setOptionIndex(null);
    setEditorContent("");
  }

  async function saveData() {
    const updateObject = {
      _id: question.id,
    };

    if (questionType === "eng") {
      updateObject.question_eng = ques;
      updateObject.options_eng = options;
    } else {
      updateObject.question_hin = ques;
      updateObject.options_hin = options;
    }

    if (image !== null && image !== undefined) {
      updateObject.image = image;
    }

    try {
      const response = await apiService.request(
        `/i/ims/aiquestion/${question.id}`,
        {
          method: "PUT",
          body: JSON.stringify(updateObject),
        },
      );

      console.log("AI QUESTION UPDATE RESPONSE:", response);

      if (typeof onSave === "function") {
        onSave(updateObject);
      }

      cancelEdit();
    } catch (err) {
      console.log("AI QUESTION UPDATE ERROR:", err);
    }
  }

  const renderMathHtml = (html, className = "") => {
    return (
      <MathJax dynamic>
        <div
          className={className}
          dangerouslySetInnerHTML={{
            __html: renderTextWithTags(html),
          }}
        />
      </MathJax>
    );
  };

  return (
    <Dialog
      size="xl"
      open={!!open}
      handler={cancelEdit}
      className="bg-transparent shadow-none"
    >
      <Card className="mx-auto w-full max-h-[80vh] overflow-y-auto">
        <CardBody className="flex flex-col gap-4 overflow-y-auto">
          <MathJaxContext config={mathJaxConfig}>
            {editState ? (
              <div>
                <QuillEditorNew
                  initialValue={editState.content || ""}
                  changeContent={setEditorContent}
                  contentType={contentType}
                />
              </div>
            ) : (
              <div>
                {/* Question */}
                <div className="flex justify-between gap-4">
                  <div className="w-full">
                    <span className="font-semibold text-xl">Question:</span>

                    {renderMathHtml(ques, "pl-2 text-base leading-relaxed")}
                  </div>

                  <div className="flex justify-center items-start">
                    <MdEdit
                      className="size-8 cursor-pointer hover:size-10"
                      onClick={() => {
                        setEditState({ content: ques });
                        setContentType("ques");
                      }}
                    />
                  </div>
                </div>

                {/* Options */}
                <div className="mt-3">
                  {options.map((option, i) => (
                    <div className="flex justify-between gap-4 mb-2" key={i}>
                      <div className="w-full">
                        <span className="font-semibold">Option {i + 1}</span>

                        {renderMathHtml(option, "pl-3 text-base")}
                      </div>

                      <div>
                        <MdEdit
                          className="size-8 cursor-pointer hover:size-10"
                          onClick={() => {
                            setEditState({ content: option });
                            setOptionIndex(i);
                            setContentType("opt");
                          }}
                        />
                      </div>
                    </div>
                  ))}
                </div>

                {/* Image field if separate */}
                {image && (
                  <div className="flex justify-between gap-4 mt-3">
                    <div className="w-full">
                      <span className="font-semibold">Image</span>
                      {renderMathHtml(image, "pl-3")}
                    </div>

                    <div>
                      <Button
                        color="red"
                        size="sm"
                        onClick={() => {
                          setEditState({ content: image });
                          setContentType("img");
                        }}
                      >
                        Edit
                      </Button>
                    </div>
                  </div>
                )}
              </div>
            )}
          </MathJaxContext>
        </CardBody>

        {editState ? (
          <CardFooter className="pt-0 flex justify-center">
            <Button
              color="blue"
              size="sm"
              className="mr-2"
              onClick={() => {
                setEditState(null);
                checkForSave();
              }}
            >
              Ok
            </Button>

            <Button
              color="red"
              size="sm"
              className="ml-2"
              onClick={() => {
                setEditState(null);
                setOptionIndex(null);
                setContentType(null);
                setEditorContent("");
              }}
            >
              Discard
            </Button>
          </CardFooter>
        ) : (
          <CardFooter className="pt-0 flex justify-center">
            {enableSave && (
              <Button
                variant="filled"
                color="green"
                onClick={saveData}
                className="mr-2"
              >
                Save
              </Button>
            )}

            <Button
              variant="filled"
              color="red"
              onClick={cancelEdit}
              className="ml-2"
            >
              Cancel
            </Button>
          </CardFooter>
        )}
      </Card>
    </Dialog>
  );
}

// import React, { useState } from "react";
// import {
//   Card,
//   CardBody,
//   CardFooter,
//   Button,
//   Dialog,
// } from "@material-tailwind/react";
// import QuillEditorNew from "../QuilEditorNew";
// import apiService from "../../services/apiService";
// import { MdEdit } from "react-icons/md";

// // const serverURL = "http://localhost:3000";

// export default function ModalBody({
//   question,
//   onSave,
//   cancelEdit,
//   questionType,
//   open,
// }) {
//   const [ques, setQues] = useState(question.ques);
//   const [options, setOptions] = useState(question.options);
//   const [image, setImage] = useState(question.image);
//   const [editState, seteditState] = useState();
//   const [optionIndex, setoptionIndex] = useState(null);
//   const [contentType, setcontentType] = useState(null);
//   const [enableSave, setenableSave] = useState(false);
//   const [editorContent, seteditorContent] = useState();
//   const [questions, setQuestions] = useState(null);
//   async function saveData() {
//     const updateObject = {
//       _id: question.id,
//     };
//     if (questionType === "eng") {
//       updateObject.question_eng = stripOuterHTML(ques);
//       updateObject.options_eng = options;
//     } else {
//       updateObject.question_hin = stripOuterHTML(ques);
//       updateObject.options_hin = options;
//     }
//     if (image != null) {
//       updateObject.image = image;
//     }
//     console.log(updateObject);

//     let response;
//     try {
//       response = await apiService.request(`/i/ims/aiquestion/${question.id}`, {
//         method: "PUT",
//         body: JSON.stringify(updateObject),
//       });
//       console.log({ response });
//       onSave(updateObject);
//       cancelEdit();
//     } catch (err) {
//       console.log(err);
//     }
//   }
//   function onSave(updatedQues) {
//     console.log({ updatedQues });
//     setQuestions((prev) => {
//       return prev.map((q) => {
//         return q.id === updatedQues._id ? { ...q, ...updatedQues } : q;
//       });
//     });
//     console.log("Updated Questions: ", questions);
//   }
//   function stripOuterHTML(htmlString) {
//     const div = document.createElement("div");
//     div.innerHTML = htmlString;
//     return div.firstChild?.textContent || "";
//   }

//   function handleChange(content) {
//     //console.log("Inside handleChange, content:",content)
//     console.log(content);
//     content = stripOuterHTML(content);
//     console.log({ content });
//     if (contentType === "img") {
//       setImage(content);
//     } else if (contentType === "ques") {
//       setQues(content);
//     } else {
//       const newOptions = [...options];
//       newOptions[optionIndex] = content;
//       setOptions(newOptions);
//       console.log({ options });
//     }
//     setcontentType(null);
//     setoptionIndex(null);
//   }

//   // function checkForSave() {
//   //   handleChange(editorContent);
//   //   ;
//   //   const obj = {
//   //     id: question.id,
//   //     num: question.num,
//   //     ques: ques,
//   //     options: options,
//   //     image: image,
//   //   };

//   //   console.log("latest obj", obj);
//   //   console.log("old question", question);
//   //   const objString = JSON.stringify(obj);
//   //   const quesString = JSON.stringify(question);
//   //   //console.log({objString})
//   //   //console.log({quesString})

//   //   if (objString === quesString) {
//   //     setenableSave(false);
//   //   } else {
//   //     setenableSave(true);
//   //   }
//   // }
//   function checkForSave() {
//     const content = stripOuterHTML(editorContent);

//     let newQues = ques;
//     let newOptions = [...options];
//     let newImage = image;

//     if (contentType === "img") {
//       newImage = content;
//       setImage(content);
//     } else if (contentType === "ques") {
//       newQues = content;
//       setQues(content);
//     } else {
//       newOptions[optionIndex] = content;
//       setOptions(newOptions);
//     }

//     const obj = {
//       id: question.id,
//       num: question.num,
//       ques: newQues,
//       options: newOptions,
//       image: newImage,
//     };

//     console.log("latest obj", obj);
//     console.log("old question", question);

//     if (JSON.stringify(obj) === JSON.stringify(question)) {
//       setenableSave(false);
//     } else {
//       setenableSave(true);
//     }

//     setcontentType(null);
//     setoptionIndex(null);
//   }
//   //   function emittedVal(e) {
//   //     console.log(e);
//   //   }

//   return (
//     <>
//       <Dialog
//         size="xl"
//         open={open}
//         handler={cancelEdit}
//         className="bg-transparent shadow-none"
//       >
//         <Card className="mx-auto w-full max-h-[80vh] overflow-y-auto">
//           {" "}
//           {/* present- max-w-[24rem] added-overflow-y-auto*/}
//           <CardBody className="flex flex-col gap-4 overflow-y-auto">
//             <div>
//               {editState ? (
//                 <div>
//                   <div>
//                     <QuillEditorNew
//                       initialValue={editState.content}
//                       changeContent={seteditorContent}
//                       contentType={contentType}
//                     />{" "}
//                     {/*seteditorContent*/}
//                   </div>
//                 </div>
//               ) : (
//                 <div>
//                   {/*Question*/}
//                   <div className="flex justify-between">
//                     <div>
//                       <span className="font-semibold text-xl">Question:</span>
//                       <p
//                         dangerouslySetInnerHTML={{ __html: ques }}
//                         className="pl-2 text-lg"
//                       ></p>
//                     </div>

//                     <div className="flex justify-center items-center ">
//                       {/* <button className='bg-red-600 text-white hover:bg-red-700 pl-2 pr-2 rounded-md' onClick={()=>{editClick(0)}}>Edit</button> */}
//                       {/* <Button color="red" size="sm" onClick={()=>{seteditState({content:ques}); setcontentType('ques')}}>Edit</Button> */}
//                       <MdEdit
//                         className="size-8 cursor-pointer hover:size-10"
//                         onClick={() => {
//                           seteditState({ content: ques });
//                           setcontentType("ques");
//                         }}
//                       />
//                     </div>
//                   </div>

//                   {/*Options*/}
//                   <div>
//                     {options.map((option, i) => (
//                       <div className="flex justify-between">
//                         <div>
//                           <span className="font-semibold">Option {i + 1}</span>
//                           <p
//                             dangerouslySetInnerHTML={{ __html: option }}
//                             className="pl-3"
//                           ></p>
//                         </div>

//                         <div>
//                           {/* <Button color="red" size="sm" onClick={()=>{seteditState({content:option}); setoptionIndex(i); setcontentType('opt')}}>Edit</Button> */}
//                           <MdEdit
//                             className="size-8 cursor-pointer hover:size-10"
//                             onClick={() => {
//                               seteditState({ content: option });
//                               setoptionIndex(i);
//                               setcontentType("opt");
//                             }}
//                           />
//                         </div>
//                       </div>
//                     ))}
//                   </div>

//                   {/*Image*/}
//                   {image && (
//                     <div className="flex justify-between">
//                       <div>
//                         <p>{image}</p>
//                       </div>

//                       <div>
//                         <Button
//                           color="red"
//                           size="sm"
//                           onClick={() => {
//                             seteditState({ content: image });
//                             setcontentType("img");
//                           }}
//                         >
//                           Edit
//                         </Button>
//                       </div>
//                     </div>
//                   )}
//                 </div>
//               )}
//             </div>
//           </CardBody>
//           {editState ? (
//             <CardFooter className="pt-0 flex justify-center">
//               <Button
//                 color="blue"
//                 size="sm"
//                 className="mr-2"
//                 onClick={() => {
//                   seteditState(null);
//                   checkForSave();
//                 }}
//               >
//                 Ok
//               </Button>
//               <Button
//                 color="red"
//                 size="sm"
//                 className="ml-2"
//                 onClick={() => {
//                   seteditState(null);
//                   setoptionIndex(null);
//                   setcontentType(null);
//                 }}
//               >
//                 Discard
//               </Button>
//             </CardFooter>
//           ) : (
//             <CardFooter className="pt-0 flex justify-center">
//               {" "}
//               {/*Added - flex*/}
//               {enableSave && (
//                 <Button
//                   variant="filled"
//                   color="green"
//                   onClick={saveData}
//                   className="mr-2"
//                 >
//                   {" "}
//                   {/*fullwidth*/}
//                   Save
//                 </Button>
//               )}
//               <Button
//                 variant="filled"
//                 color="red"
//                 onClick={cancelEdit}
//                 className="ml-2"
//               >
//                 {" "}
//                 {/*fullwidth*/}
//                 Cancel
//               </Button>
//             </CardFooter>
//           )}
//         </Card>
//       </Dialog>
//     </>
//   );
// }
